import React, { useState, useEffect } from 'react';
import { Download, Upload, Loader2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

// --- Imports from your project structure ---
// Make sure these paths match your project structure
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext'; 
import { editImage, checkImageStatus } from '../../services/chatApi';
// --- Constants ---
// const STYLE_CATEGORIES = {
//     "Classical Art": ["Renaissance painting", "Baroque painting", "Classical oil painting", "Fresco painting"],
//     "Modern Art": ["Impressionist", "Post-impressionist", "Expressionist", "Cubist", "Pop art"],
//     "Photography Styles": ["Black and white photography", "Vintage photography", "Polaroid style", "Cinematic lighting"],
//     "Animation/Cartoon": ["Anime style", "Disney animation", "Comic book style", "Cyberpunk"],
//     "Digital Art": ["Pixel art", "3D render", "Concept art", "Unreal Engine 5"]
// };

const STYLE_CATEGORIES_EN = {
    "Classical Art": ["Renaissance painting", "Baroque painting", "Classical oil painting", "Fresco painting"],
    "Modern Art": ["Impressionist", "Post-impressionist", "Expressionist", "Cubist", "Pop art"],
    "Photography Styles": ["Black and white photography", "Vintage photography", "Polaroid style", "Cinematic lighting"],
    "Animation/Cartoon": ["Anime style", "Disney animation", "Comic book style", "Cyberpunk"],
    "Digital Art": ["Pixel art", "3D render", "Concept art", "Unreal Engine 5"]
};

// 2. GERMAN (Display Only)
const STYLE_CATEGORIES_DE = {
    "Klassische Kunst": ["Renaissance-Malerei", "Barockmalerei", "Klassische Ölmalerei", "Freskomalerei"],
    "Moderne Kunst": ["Impressionismus", "Post-Impressionismus", "Expressionismus", "Kubismus", "Pop Art"],
    "Fotografie-Stile": ["Schwarz-Weiß-Fotografie", "Vintage-Fotografie", "Polaroid-Stil", "Filmische Beleuchtung"],
    "Animation/Cartoon": ["Anime-Stil", "Disney-Animation", "Comic-Stil", "Cyberpunk"],
    "Digitale Kunst": ["Pixelkunst", "3D-Rendering", "Konzeptkunst", "Unreal Engine 5"]
};

// 3. FRENCH (Display Only)
const STYLE_CATEGORIES_FR = {
    "Art Classique": ["Peinture de la Renaissance", "Peinture baroque", "Peinture à l'huile classique", "Peinture à fresque"],
    "Art Moderne": ["Impressionniste", "Post-impressionniste", "Expressionniste", "Cubiste", "Pop art"],
    "Styles Photographiques": ["Photographie en noir et blanc", "Photographie vintage", "Style Polaroid", "Éclairage cinématographique"],
    "Animation/Dessin animé": ["Style Anime", "Animation Disney", "Style bande dessinée", "Cyberpunk"],
    "Art Numérique": ["Pixel art", "Rendu 3D", "Concept art", "Unreal Engine 5"]
};

// --- Sub-Components for Access Control ---

const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            // Removes "data:image/png;base64," prefix to get raw string
            const base64String = reader.result.split(',')[1]; 
            resolve(base64String);
        };
        reader.onerror = (error) => reject(error);
    });
};

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


// --- Main Component ---

const ImageEditor = () => {
    const { t, i18n } = useTranslation();
    const { user, setCredits } = useAuth(); // Get user from context

    // State
    const [inputImage, setInputImage] = useState(null);
    const [inputImagePreview, setInputImagePreview] = useState(null);
    const [styleRefImage, setStyleRefImage] = useState(null);
    const [styleRefPreview, setStyleRefPreview] = useState(null);
    
    // Default editType key (internal logic key)
    const [editType, setEditType] = useState("artistic");
    const [activeStyleTab, setActiveStyleTab] = useState("preset");
    
    // Style Logic State
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedStyle, setSelectedStyle] = useState("");
    const [customStyleText, setCustomStyleText] = useState("");
    
    // Prompt Inputs for other modes
    const [objectPrompt, setObjectPrompt] = useState("");
    const [bgPrompt, setBgPrompt] = useState("");
    const [textPrompt, setTextPrompt] = useState("");
    const [customPrompt, setCustomPrompt] = useState("");
    
    const [outputFormat, setOutputFormat] = useState("PNG");
    
    // Processing State
    const [isLoading, setIsLoading] = useState(false);
    const [resultImage, setResultImage] = useState(null);
    const [error, setError] = useState(null);

    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    
    const imageFormats = ['PNG', 'JPEG', 'WebP', 'GIF', 'TIFF', 'BMP'];


    useEffect(() => {
        let interval = null;
        if (taskId && taskStatus === 'processing') {
            interval = setInterval(async () => {
                try {
                    const statusResponse = await checkImageStatus(taskId);
                    setTaskStatus(statusResponse.status);

                    if (statusResponse.status === 'completed') {
                        setResultImage({
                            data: `data:image/${statusResponse.image_format?.toLowerCase() || 'png'};base64,${statusResponse.image_data}`,
                            format: statusResponse.image_format || outputFormat
                        });
                        
                        // Update credits if returned
                        if (statusResponse.new_credits !== undefined) {
                            setCredits(statusResponse.new_credits);
                        }
                        
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        clearInterval(interval);

                    } else if (statusResponse.status === 'failed') {
                        setError(statusResponse.error || t('imageGenerator.errorGeneric'));
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                        clearInterval(interval);
                    }
                } catch (err) {
                    console.error('Error checking task status:', err);
                    setError(t('imageGenerator.statusCheckError'));
                    setTaskId(null);
                    setIsLoading(false);
                    setTaskStatus(null);
                    clearInterval(interval);
                }
            }, 2000); // Poll every 2 seconds
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [taskId, taskStatus, outputFormat, setCredits, t]);


    // --- File Handlers ---
    const handleImageUpload = (e, isStyleRef = false) => {
        const file = e.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            if (isStyleRef) {
                setStyleRefImage(file);
                setStyleRefPreview(url);
            } else {
                setInputImage(file);
                setInputImagePreview(url);
                setResultImage(null);
            }
        }
    };

    // --- Prompt Construction Logic ---
    const constructFinalPrompt = () => {
        let finalPrompt = "";

        if (editType === "artistic") {
            let styleName = "";
            if (activeStyleTab === 'custom' && customStyleText) {
                styleName = customStyleText;
            } else if (activeStyleTab === 'preset' && selectedStyle) {
                styleName = selectedStyle;
            }

            if (!styleName && !styleRefImage) return null;

            if (styleRefImage) {
                finalPrompt = `Using the provided style reference image, transform the input image to a '${styleName}' style, while maintaining the original composition.`;
            } else {
                finalPrompt = `Transform the image to a '${styleName}' style, maintaining the same composition and subject.`;
            }
        } 
        else if (editType === "objects") finalPrompt = objectPrompt;
        else if (editType === "background") finalPrompt = bgPrompt;
        else if (editType === "text") finalPrompt = textPrompt;
        else finalPrompt = customPrompt;

        return finalPrompt;
    };

    // --- API Interaction (Mock) ---
    const handleEdit = async () => {
        const prompt = constructFinalPrompt();
        if (!prompt) {
            setError(t('imageEditor.errors.missingPrompt'));
            return;
        }
        if (!inputImage) {
            setError(t('imageEditor.errors.missingImage'));
            return;
        }

        setIsLoading(true);
        setError(null);
        setResultImage(null);
        setTaskStatus(null);
        setTaskId(null);

        try {
            // 1. Convert Input Image to Base64
            const base64Input = await fileToBase64(inputImage);
            
            // 2. Convert Reference Image (if exists)
            let base64Ref = null;
            if (styleRefImage) {
                base64Ref = await fileToBase64(styleRefImage);
            }

            // 3. Prepare Payload
            const payload = {
                prompt: prompt,
                input_image: base64Input,
                style_reference: base64Ref,
                image_format: outputFormat
            };

            // 4. Call API
            const result = await editImage(payload);

            if (result.task_id) {
                setTaskId(result.task_id);
                setTaskStatus('processing');
            } else {
                setError(t('imageGenerator.errorGeneric'));
                setIsLoading(false);
            }

        } catch (err) {
            console.error(err);
            setError(err.message || t('imageGenerator.errorGeneric'));
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!resultImage) return;
        const link = document.createElement('a');
        link.href = resultImage.data;
        const formatExtensions = {
            'PNG': 'png', 'JPEG': 'jpg', 'WEBP': 'webp',
            'GIF': 'gif', 'TIFF': 'tiff', 'BMP': 'bmp'
        };
        const extension = formatExtensions[resultImage.format] || 'png';
        link.download = `edited_image.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClear = () => {
        setInputImage(null);
        setInputImagePreview(null);
        setStyleRefImage(null);
        setStyleRefPreview(null);
        setResultImage(null);
        setError(null);
        setObjectPrompt("");
        setBgPrompt("");
        setTextPrompt("");
        setCustomPrompt("");
        setCustomStyleText("");
        setSelectedCategory("");
        setSelectedStyle("");
        setTaskId(null);
        setTaskStatus(null);
    };

    // --- UI Render Helpers ---
    const renderEditOptions = () => {
        const editTypeKeys = ["artistic", "objects", "background", "text", "custom"];
        
        return (
            <div className="mt-6">
                <label className="block text-sm font-medium mb-2 text-gray-800">
                    {t('imageEditor.editTypeLabel')}
                </label>
                <div className="flex flex-wrap gap-2">
                    {editTypeKeys.map(key => (
                        <button
                            key={key}
                            onClick={() => setEditType(key)}
                            disabled={isLoading}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                editType === key 
                                ? 'bg-orange-500 text-white' 
                                : 'border border-gray-600 hover:bg-orange-500 hover:text-white text-gray-800'
                            }`}
                        >
                            {t(`imageEditor.types.${key}`)}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {/* Access Control Check */}
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto bg-brand-white">
                    <h3 className="text-3xl font-bold text-orange-500">
                        {t('imageEditor.title')}
                    </h3>
                    <p className="mt-2 text-gray-800">
                        {t('imageEditor.description')}
                    </p>

                    {/* Info Box */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                            {t('imageEditor.tip')}
                        </p>
                    </div>

                    {/* Upload Image Section */}
                    <div className="mt-6">
                        <label className="block text-sm font-medium mb-2 text-gray-800">
                            {t('imageEditor.uploadLabel')}
                        </label>
                        <div className="flex items-center justify-center w-full">
                            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-600 rounded-md cursor-pointer bg-white hover:bg-gray-50 transition-colors">
                                {inputImagePreview ? (
                                    <img src={inputImagePreview} alt="Preview" className="h-full w-full object-contain rounded-md p-2" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-12 h-12 mb-4 text-gray-500" />
                                        <p className="text-sm text-gray-600 font-medium">{t('imageEditor.dropzoneText')}</p>
                                        <p className="text-xs text-gray-500 mt-1">{t('imageEditor.dropzoneHint')}</p>
                                    </div>
                                )}
                                <input type="file" className="hidden" onChange={(e) => handleImageUpload(e)} accept="image/*" disabled={isLoading} />
                            </label>
                        </div>
                    </div>

                    {/* Edit Type Selection */}
                    {renderEditOptions()}

                    {/* Dynamic Form Fields based on Edit Type */}
                    <div className="mt-6">
                        {editType === "artistic" && (
                            <div>
                                <div className="flex border-b border-gray-300 mb-4">
                                    <button 
                                        className={`flex-1 py-2 text-sm font-medium ${activeStyleTab === 'preset' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-gray-600'}`}
                                        onClick={() => setActiveStyleTab('preset')}
                                        disabled={isLoading}
                                    >
                                        {t('imageEditor.styleMode.presetTab')}
                                    </button>
                                    <button 
                                        className={`flex-1 py-2 text-sm font-medium ${activeStyleTab === 'custom' ? 'border-b-2 border-orange-500 text-orange-500' : 'text-gray-600'}`}
                                        onClick={() => setActiveStyleTab('custom')}
                                        disabled={isLoading}
                                    >
                                        {t('imageEditor.styleMode.customTab')}
                                    </button>
                                </div>

                                {activeStyleTab === 'preset' ? (
                                <div className="space-y-4">
                                    {/* CATEGORY SELECT */}
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-800">{t('imageEditor.styleMode.categoryLabel')}</label>
                                        <select 
                                            className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none rounded-md"
                                            value={selectedCategory}
                                            onChange={(e) => {
                                                setSelectedCategory(e.target.value);
                                                setSelectedStyle(""); // Reset style when category changes
                                            }}
                                            disabled={isLoading}
                                        >
                                            <option value="">{t('imageEditor.styleMode.selectCategoryPlaceholder')}</option>
                                            
                                            {/* Map over ENGLISH keys to keep internal value English */}
                                            {Object.keys(STYLE_CATEGORIES_EN).map((catEn, index) => {
                                                // Determine which display object to use
                                                const displayObj = i18n.language === 'de' ? STYLE_CATEGORIES_DE 
                                                                 : i18n.language === 'fr' ? STYLE_CATEGORIES_FR 
                                                                 : STYLE_CATEGORIES_EN;
                                                
                                                // Get the Translated Label using the same index
                                                const displayLabel = Object.keys(displayObj)[index] || catEn;

                                                return (
                                                    <option key={catEn} value={catEn}>
                                                        {displayLabel}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    {/* STYLE SELECT */}
                                    {selectedCategory && (
                                        <div>
                                            <label className="block text-sm font-medium mb-2 text-gray-800">{t('imageEditor.styleMode.styleLabel')}</label>
                                            <select 
                                                className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none rounded-md"
                                                value={selectedStyle}
                                                onChange={(e) => setSelectedStyle(e.target.value)}
                                                disabled={isLoading}
                                            >
                                                <option value="">{t('imageEditor.styleMode.selectStylePlaceholder')}</option>
                                                
                                                {/* Map over the English array for the selected English category */}
                                                {STYLE_CATEGORIES_EN[selectedCategory].map((styleEn, index) => {
                                                    // 1. Find the display object
                                                    const displayObj = i18n.language === 'de' ? STYLE_CATEGORIES_DE 
                                                                     : i18n.language === 'fr' ? STYLE_CATEGORIES_FR 
                                                                     : STYLE_CATEGORIES_EN;

                                                    // 2. Find the corresponding key in the display object (by index of category)
                                                    const enKeys = Object.keys(STYLE_CATEGORIES_EN);
                                                    const catIndex = enKeys.indexOf(selectedCategory);
                                                    const displayCatKey = Object.keys(displayObj)[catIndex];

                                                    // 3. Get the specific style translation by index
                                                    const displayLabel = displayObj[displayCatKey][index] || styleEn;

                                                    return (
                                                        <option key={styleEn} value={styleEn}>
                                                            {displayLabel}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2 text-gray-800">{t('imageEditor.styleMode.customLabel')}</label>
                                            <input 
                                                type="text" 
                                                placeholder={t('imageEditor.styleMode.customPlaceholder')}
                                                className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none rounded-md"
                                                value={customStyleText}
                                                onChange={e => setCustomStyleText(e.target.value)}
                                                disabled={isLoading}
                                            />
                                        </div>
                                        <div className="border-t border-gray-300 pt-4">
                                            <label className="block text-sm font-medium mb-2 text-gray-800">
                                                {t('imageEditor.styleMode.refImageLabel')}
                                            </label>
                                            <input 
                                                type="file" 
                                                onChange={(e) => handleImageUpload(e, true)} 
                                                className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-500 file:text-white hover:file:bg-orange-600"
                                                accept="image/*"
                                                disabled={isLoading}
                                            />
                                            {styleRefPreview && (
                                                <img src={styleRefPreview} alt="Reference" className="h-32 mt-3 rounded-md border border-gray-300" />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {editType === "objects" && (
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-800">
                                    {t('imageEditor.prompts.objectsLabel')}
                                </label>
                                <textarea 
                                    className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[120px] resize-y rounded-md"
                                    placeholder={t('imageEditor.prompts.objectsPlaceholder')}
                                    value={objectPrompt}
                                    onChange={e => setObjectPrompt(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        )}
                        
                        {editType === "background" && (
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-800">
                                    {t('imageEditor.prompts.bgLabel')}
                                </label>
                                <textarea 
                                    className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[120px] resize-y rounded-md"
                                    placeholder={t('imageEditor.prompts.bgPlaceholder')}
                                    value={bgPrompt}
                                    onChange={e => setBgPrompt(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        {editType === "text" && (
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-800">
                                    {t('imageEditor.prompts.textLabel')}
                                </label>
                                <textarea 
                                    className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[120px] resize-y rounded-md"
                                    placeholder={t('imageEditor.prompts.textPlaceholder')}
                                    value={textPrompt}
                                    onChange={e => setTextPrompt(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        {editType === "custom" && (
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-800">
                                    {t('imageEditor.prompts.customLabel')}
                                </label>
                                <textarea 
                                    className="w-full p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[120px] resize-y rounded-md"
                                    placeholder={t('imageEditor.prompts.customPlaceholder')}
                                    value={customPrompt}
                                    onChange={e => setCustomPrompt(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                        )}
                    </div>

                    {/* Output Format */}
                    <div className="mt-6 bg-brand-white">
                        <label className="block text-sm font-medium mb-2 text-gray-800">
                            {t('imageEditor.formatLabel')}
                        </label>
                        <select 
                            value={outputFormat} 
                            onChange={e => setOutputFormat(e.target.value)}
                            className="w-full md:w-64 p-3 bg-white text-gray-800 border border-gray-600 focus:ring-2 focus:ring-orange-500 focus:outline-none rounded-md"
                            disabled={isLoading}
                        >
                            {imageFormats.map(format => (
                                <option key={format} value={format}>{format}</option>
                            ))}
                        </select>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mt-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4 mt-6 bg-brand-white">
                        <button
                            onClick={handleEdit}
                            disabled={isLoading || !inputImage}
                            className="px-6 py-2 bg-brand-white border border-orange-500 hover:bg-orange-500 text-gray-800 hover:text-white font-semibold rounded-md transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed disabled:border-gray-500 disabled:text-white flex items-center"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    {t('imageEditor.buttons.processing')}
                                </>
                            ) : (
                                <>
                                    <Sparkles className="mr-2" size={20} />
                                    {t('imageEditor.buttons.apply')}
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-6 py-2 bg-brand-white border border-gray-600 hover:bg-orange-500 hover:text-white text-gray-800 font-semibold rounded-md transition-colors"
                            disabled={isLoading}
                        >
                            {t('imageEditor.buttons.clear')}
                        </button>
                    </div>

                    {/* Display Result Image */}
                    {resultImage && (
                        <div className="mt-10 bg-brand-white">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-2xl font-semibold text-gray-800">
                                    {t('imageEditor.results.title')}
                                </h4>
                            </div>
                            
                            <div className="relative border border-gray-300 rounded-md overflow-hidden bg-white shadow-lg">
                                <img 
                                    src={resultImage.data} 
                                    alt="Edited Result" 
                                    className="w-full h-auto"
                                />
                            </div>

                            {/* Image Info */}
                            <div className="mt-2 text-sm text-gray-600">
                                {t('imageEditor.results.formatInfo', { format: resultImage.format })}
                            </div>

                            {/* Download Button */}
                            <div className="flex gap-4 mt-6">
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('imageEditor.buttons.download', { format: resultImage.format })}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageEditor;
